/**
 * Account Navigation Web Component
 * Handles responsive navigation for account dashboard
 */
export default function accountNavigation() {
  return {
    activeTab: 'dashboard',
    tabButtons: [],
    tabContents: [],
    observer: null,
    
    init() {
      this.tabButtons = this.$el.querySelectorAll('.nav-tab');
      this.tabContents = document.querySelectorAll('[id$="-content"]');
      
      // Initialize URL-based navigation
      this.initUrlNavigation();
      
      // Set up event listeners
      this.setupEventListeners();

      // Watch for DOM changes to handle dynamic content
      this.observer = new MutationObserver(() => this.updateFocusInTabs());
      this.tabContents.forEach(content => {
        this.observer.observe(content, { childList: true, subtree: true });
      });
    },

    destroy() {
      if (this.observer) {
        this.observer.disconnect();
      }
    },
    
    setupEventListeners() {
      // Handle tab clicks
      this.tabButtons.forEach(button => {
        button.addEventListener('click', (e) => {
          e.preventDefault();
          const targetTab = button.dataset.tab;
          if (targetTab) {
            this.showTab(targetTab);
          }
        });
        
        // Handle keyboard navigation
        button.addEventListener('keydown', (e) => {
          const currentIndex = Array.from(this.tabButtons).indexOf(button);
          let targetIndex;
          
          switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
              e.preventDefault();
              targetIndex = currentIndex > 0 ? currentIndex - 1 : this.tabButtons.length - 1;
              break;
            case 'ArrowRight':
            case 'ArrowDown':
              e.preventDefault();
              targetIndex = currentIndex < this.tabButtons.length - 1 ? currentIndex + 1 : 0;
              break;
            case 'Home':
              e.preventDefault();
              targetIndex = 0;
              break;
            case 'End':
              e.preventDefault();
              targetIndex = this.tabButtons.length - 1;
              break;
            case 'Enter':
            case ' ': {
              e.preventDefault();
              const targetTab = button.dataset.tab;
              if (targetTab) {
                this.showTab(targetTab);
              }
              return;
            }
            default:
              return;
          }
          
          if (targetIndex !== undefined) {
            this.tabButtons[targetIndex].focus();
          }
        });
      });
    },
    
    updateFocusInTabs() {
      this.tabContents.forEach(content => {
        const isActive = content.id === `${this.activeTab}-content`;
        const focusableElements = content.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');

        if (isActive) {
          // Restore focusability for active tab
          const elementsToRestore = content.querySelectorAll('[data-original-tabindex]');
          elementsToRestore.forEach(element => {
            const originalTabindex = element.getAttribute('data-original-tabindex');
            if (originalTabindex === '0') {
              element.removeAttribute('tabindex');
            } else {
              element.setAttribute('tabindex', originalTabindex);
            }
            element.removeAttribute('data-original-tabindex');
          });
        } else {
          // Make all focusable elements in inactive tabs non-focusable
          focusableElements.forEach(element => {
            if (element.hasAttribute('data-original-tabindex')) return;
            const originalTabindex = element.getAttribute('tabindex');
            element.setAttribute('data-original-tabindex', originalTabindex === null ? '0' : originalTabindex);
            element.setAttribute('tabindex', '-1');
          });
        }
      });
    },

    showTab(targetTab, updateUrl = true) {
      this.activeTab = targetTab;
      
      // Update URL if requested
      if (updateUrl) {
        window.history.pushState(null, null, `#${targetTab}`);
      }
      
      // Hide all content
      this.tabContents.forEach(content => {
        if (!content.id.startsWith(targetTab)) {
          content.classList.add('tab-content-hidden');
          content.classList.remove('tab-content-visible');
          content.setAttribute('aria-hidden', 'true');
          // Make all interactive elements in hidden tabs non-focusable
          const focusableElements = content.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
          focusableElements.forEach(el => {
            el.setAttribute('tabindex', '-1');
          });
        }
      });
      
      // Show target content
      const targetContent = document.getElementById(`${targetTab}-content`);
      if (targetContent) {
        targetContent.classList.remove('tab-content-hidden');
        targetContent.classList.add('tab-content-visible');
        targetContent.setAttribute('aria-hidden', 'false');
        
        // Make all interactive elements in the active tab focusable
        const focusableElements = targetContent.querySelectorAll('button, [href], input, select, textarea, [tabindex="-1"]');
        focusableElements.forEach(el => {
          // Only modify elements that don't have a tabindex set already
          if (el.getAttribute('tabindex') === '-1') {
            el.removeAttribute('tabindex');
          }
        });
        
        // Find the first focusable element in the content
        const firstFocusable = targetContent.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
          firstFocusable.focus();
        } else {
          targetContent.setAttribute('tabindex', '-1');
          targetContent.focus();
        }
      }
      
      // Update button states
      this.tabButtons.forEach(button => {
        const isActive = (button.dataset.tab === targetTab) || (button.dataset.tab === 'order-history' && targetTab === 'order-details');
        button.setAttribute('aria-selected', isActive);
        button.setAttribute('tabindex', isActive ? '0' : '-1');
        
        if (isActive) {
          // Active styles - white text with underline
          button.classList.remove('hover:underline');
          button.classList.add('underline', 'underline-offset-[0.5rem]');
        } else {
          // Inactive styles - blue text, underline on hover
          button.classList.remove('underline', 'underline-offset-[0.5rem]');
          button.classList.add('hover:underline');
        }
      });
    },
    
    // Initialize URL-based navigation
    initUrlNavigation() {
      // Set initial tab based on URL hash
      this.setTabFromUrl();
      
      // Listen for hash changes (browser back/forward)
      window.addEventListener('hashchange', () => {
        this.setTabFromUrl();
      });
    },
    
    setTabFromUrl() {
      const hash = window.location.hash.replace('#', '');
      const validTabs = ['dashboard', 'next-box', 'subscriptions', 'order-history', 'order-details'];
      const orderId = new URLSearchParams(window.location.search).get('orderId');
      
      if (hash && validTabs.includes(hash)) {
        this.showTab(hash, false); // Don't update URL when setting from URL
      } else {
        if (orderId) {
          this.showTab('order-details', false); // Default tab
        } else {
          this.showTab('dashboard', false); // Default tab
        }
      }
    }
  }
}
